import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { Rocket, PlugZap, PenLine, PartyPopper } from "lucide-react";
import i18n from "i18";
import _request from "actions/_request";
import { isDismissed, dismiss, isWelcomed, setWelcomed } from "./state";
import "./style.css";

const TOTAL_STEPS = 4;

/* ─── Steps config ────────────────────────────────────────── */
const STEPS = [
  {
    key: "welcome",
    Icon: Rocket,
    color: "#42C0C7",
    actionKey: "actionStart",
    hasSkip: false,
    navigateTo: null,
  },
  {
    key: "connectAccount",
    Icon: PlugZap,
    color: "#6C8EFF",
    actionKey: "actionConnect",
    hasSkip: true,
    navigateTo: "/app/accounts",
  },
  {
    key: "createPost",
    Icon: PenLine,
    color: "#FF8C6B",
    actionKey: "actionCreate",
    hasSkip: true,
    navigateTo: "/app/post",
  },
  {
    key: "done",
    Icon: PartyPopper,
    color: "#42C0C7",
    actionKey: "actionDone",
    hasSkip: false,
    navigateTo: "/app/dashboard",
  },
];

const t = (subKey) => i18n.t(`onboarding.${subKey}`);

/* ─── Progress hook — fetches real data ────────────────────── */
function useProgress() {
  const [hasAccounts, setHasAccounts] = useState(false);
  const [hasPosts, setHasPosts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissedState] = useState(isDismissed);

  const refresh = useCallback(async () => {
    if (isDismissed()) return;
    setLoading(true);
    try {
      const [accResp, postsResp] = await Promise.all([
        _request.accounts.getAll(),
        _request.postTasks.getAll(),
      ]);
      setHasAccounts((accResp?.data?.items?.length || 0) > 0);
      setHasPosts((postsResp?.data?.items?.length || 0) > 0);
    } catch (_) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dismissed) refresh();
  }, [dismissed, refresh]);

  // Refresh when window regains focus (user returns after connecting an account)
  useEffect(() => {
    const onFocus = () => { if (!isDismissed()) refresh(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Sync when dismissed from another place
  useEffect(() => {
    const handler = () => setDismissedState(isDismissed());
    window.addEventListener("onboarding-change", handler);
    return () => window.removeEventListener("onboarding-change", handler);
  }, []);

  const handleDismiss = () => {
    dismiss();
    setDismissedState(true);
  };

  const computedStep = useCallback(() => {
    if (!isWelcomed()) return 0;
    if (!hasAccounts) return 1;
    if (!hasPosts) return 2;
    return 3;
  }, [hasAccounts, hasPosts]);

  return { hasAccounts, hasPosts, loading, dismissed, handleDismiss, refresh, computedStep };
}

/* ─── Header Badge ─────────────────────────────────────────── */
function OnboardingBadge({ onOpen, onDismiss, hasAccounts, hasPosts, loading }) {
  const stepsStatus = [isWelcomed(), hasAccounts, hasPosts];
  const completedCount = stepsStatus.filter(Boolean).length;
  const isAllDone = completedCount === 3;

  return (
    <Box className="ob-badge" onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
      {loading ? (
        <CircularProgress size={11} sx={{ color: "#42C0C7", flexShrink: 0 }} />
      ) : (
        <Box className="ob-badge-steps">
          {stepsStatus.map((done, i) => (
            <Box key={i} className={`ob-badge-dot ${done ? "ob-badge-dot--done" : ""}`}>
              {done && <CheckIcon sx={{ fontSize: 8, color: "#fff" }} />}
            </Box>
          ))}
        </Box>
      )}
      <Typography className="ob-badge-label">
        {isAllDone ? t("badgeDone") : t("badgeLabel")}
        {!isAllDone && <span className="ob-badge-count">{completedCount}/3</span>}
      </Typography>
      <Tooltip title={t("dismiss")} placement="bottom" arrow>
        <Box
          className="ob-badge-close"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          role="button"
          aria-label="dismiss"
        >
          <CloseIcon sx={{ fontSize: 11 }} />
        </Box>
      </Tooltip>
    </Box>
  );
}

/* ─── Main Component ─────────────────────────────────────── */
export default function Onboarding() {
  const navigate = useNavigate();
  const { hasAccounts, hasPosts, loading, dismissed, handleDismiss, refresh, computedStep } = useProgress();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [slotEl, setSlotEl] = useState(null);
  const [animating, setAnimating] = useState(false);
  const autoOpenedRef = useRef(false);

  // Find portal slot after mount
  useEffect(() => {
    setSlotEl(document.getElementById("onboarding-slot"));
  }, []);

  // First-ever visit: auto-open wizard once, only when data is loaded
  useEffect(() => {
    if (!autoOpenedRef.current && !isDismissed() && !isWelcomed() && !loading) {
      autoOpenedRef.current = true;
      setStep(0);
      setWizardOpen(true);
    }
  }, [loading]);

  // When data changes while wizard is open, advance step
  useEffect(() => {
    if (wizardOpen) {
      const s = computedStep();
      if (s !== step) goToStep(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccounts, hasPosts]);

  const openWizard = () => {
    refresh().then(() => {
      setStep(computedStep());
      setWizardOpen(true);
    });
  };

  const closeWizard = () => setWizardOpen(false);

  const goToStep = (newStep) => {
    setAnimating(true);
    setTimeout(() => {
      setStep(newStep);
      setAnimating(false);
    }, 180);
  };

  const handleAction = () => {
    const current = STEPS[step];
    if (step === 0) {
      setWelcomed();
      goToStep(computedStep() > 0 ? computedStep() : 1);
      return;
    }
    if (step === 3) {
      handleDismiss();
      if (current.navigateTo) navigate(current.navigateTo);
      return;
    }
    if (current.navigateTo) {
      navigate(current.navigateTo);
      closeWizard();
    }
  };

  const handleSkip = () => {
    const next = computedStep();
    if (step < 3) goToStep(next > step ? next : step + 1);
    else handleDismiss();
  };

  if (dismissed) return null;

  const current = STEPS[step] || STEPS[0];
  const { Icon, color, key, actionKey, hasSkip } = current;
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <>
      {/* Header badge rendered via portal into the header slot */}
      {slotEl &&
        ReactDOM.createPortal(
          <OnboardingBadge
            onOpen={openWizard}
            onDismiss={handleDismiss}
            hasAccounts={hasAccounts}
            hasPosts={hasPosts}
            loading={loading}
          />,
          slotEl,
        )}

      {/* Wizard Dialog */}
      <Dialog
        open={wizardOpen}
        onClose={closeWizard}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: "ob-paper",
          sx: {
            backgroundColor: "var(--background-menu)",
            borderRadius: "16px",
            border: "1px solid var(--week-color)",
            overflow: "hidden",
          },
        }}
      >
        {/* Top progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            backgroundColor: "var(--week-color)",
            "& .MuiLinearProgress-bar": {
              backgroundColor: color,
              transition: "transform 0.5s ease, background-color 0.4s",
            },
          }}
        />

        {/* Close (temporary) */}
        <Box className="ob-close">
          <IconButton onClick={closeWizard} size="small">
            <CloseIcon sx={{ color: "var(--text-color)", fontSize: 17, opacity: 0.4 }} />
          </IconButton>
        </Box>

        <DialogContent className={`ob-content${animating ? " ob-content--fade" : ""}`}>
          {/* Step dots */}
          <Box className="ob-dots">
            {STEPS.map((s, i) => (
              <Box
                key={i}
                className={`ob-dot ${i === step ? "ob-dot--active" : ""} ${i < step ? "ob-dot--passed" : ""}`}
                sx={{
                  backgroundColor: i === step ? color : i < step ? "#42C0C7" : "var(--week-color)",
                  opacity: i < step ? 0.45 : 1,
                }}
              />
            ))}
          </Box>

          {/* Icon */}
          <Box className="ob-icon-wrap" sx={{ backgroundColor: `${color}1A` }}>
            <Icon size={48} color={color} strokeWidth={1.5} />
          </Box>

          {/* Text */}
          <Typography className="ob-title" sx={{ color: "var(--text-color)" }}>
            {t(`steps.${key}.title`)}
          </Typography>
          <Typography className="ob-desc" sx={{ color: "var(--text-color)" }}>
            {t(`steps.${key}.desc`)}
          </Typography>

          {/* Checklist on welcome */}
          {key === "welcome" && (
            <Box className="ob-checklist">
              {[0, 1, 2].map((i) => (
                <Box key={i} className="ob-checklist-row">
                  <Box className="ob-checklist-bullet" sx={{ backgroundColor: color }} />
                  <Typography sx={{ color: "var(--text-color)", fontSize: 14, fontFamily: "Montserrat" }}>
                    {t(`steps.welcome.list${i}`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Actions */}
          <Box className="ob-actions">
            <Button
              variant="contained"
              fullWidth
              disableElevation
              className="ob-btn-primary"
              onClick={handleAction}
              sx={{
                backgroundColor: color,
                "&:hover": { backgroundColor: color, filter: "brightness(0.9)" },
              }}
            >
              {t(actionKey)}
            </Button>
            {hasSkip && (
              <Button
                variant="text"
                fullWidth
                className="ob-btn-skip"
                onClick={handleSkip}
                sx={{ color: "var(--text-color)", opacity: 0.4 }}
              >
                {t("skip")}
              </Button>
            )}
          </Box>

          {/* Counter */}
          <Typography className="ob-counter" sx={{ color: "var(--text-color)" }}>
            {step + 1} / {TOTAL_STEPS}
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}

